import { useEffect, useMemo, useRef } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Skeleton } from '@/components/ui/Skeleton'
import { AlertRow } from './AlertRow'
import { AlertsTableHeader } from './AlertsTableHeader'
import { EmptyQueue } from './EmptyQueue'
import { QueueError } from './QueueError'
import type { AlertFilters, AlertSummary, SortKey } from '../../types'

const ROW_HEIGHT = 40

interface Props {
  alerts: AlertSummary[]
  loading: boolean
  error: Error | null
  filters: AlertFilters
  sort: SortKey[]
  selectedIds: Set<string>
  focusedIndex: number
  recentlyChangedIds: Set<string>
  onSort: (field: SortKey['field'], multi: boolean) => void
  onToggleSelect: (id: string, range: boolean) => void
  onClearFilters: () => void
  onResetToMyQueue: () => void
  onRetry: () => void
  /** Fetch the next page when the user scrolls within ~10 rows of the bottom. */
  onLoadMore?: () => void
  /** True when the next page is currently loading (renders a footer row). */
  isFetchingNextPage?: boolean
  /** True when no more pages are available — disables onLoadMore. */
  hasNextPage?: boolean
  /** Imperative scroll-to-top, used by the "X new alerts" toast. */
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>
}

/**
 * AF-126 — virtualized alerts queue.
 *
 * Body uses TanStack Virtual; only ~30 rows in DOM at any time even with
 * 5000-alert datasets. Header is rendered above and uses the same column
 * grid template so columns line up exactly.
 */
export function AlertsTable({
  alerts,
  loading,
  error,
  filters,
  sort,
  selectedIds,
  focusedIndex,
  recentlyChangedIds,
  onSort,
  onToggleSelect,
  onClearFilters,
  onResetToMyQueue,
  onRetry,
  onLoadMore,
  isFetchingNextPage,
  hasNextPage,
  scrollContainerRef,
}: Props) {
  const internalRef = useRef<HTMLDivElement | null>(null)
  const parentRef = scrollContainerRef ?? internalRef

  // TanStack Virtual's `useVirtualizer` returns memoised callbacks
  // that the React Compiler can't safely re-memoise (well-known
  // incompatibility — the compiler intentionally skips the call
  // site). The disable is informational rather than risky: the
  // library handles memoisation internally.
  // eslint-disable-next-line react-hooks/incompatible-library
  const rowVirtualizer = useVirtualizer({
    count: alerts.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
  })

  // Keep keyboard-focused row in view.
  useEffect(() => {
    if (focusedIndex < 0) return
    rowVirtualizer.scrollToIndex(focusedIndex, { align: 'auto' })
  }, [focusedIndex, rowVirtualizer])

  // Trigger fetchNextPage when the bottom-most virtual row index is within
  // 10 of the end of the loaded set. This covers both scroll and resize.
  const virtualItems = rowVirtualizer.getVirtualItems()
  useEffect(() => {
    if (!onLoadMore || !hasNextPage || isFetchingNextPage) return
    const last = virtualItems[virtualItems.length - 1]
    if (!last) return
    if (last.index >= alerts.length - 10) {
      onLoadMore()
    }
  }, [virtualItems, alerts.length, hasNextPage, isFetchingNextPage, onLoadMore])

  const orderedIds = useMemo(() => alerts.map((a) => a.id), [alerts])

  return (
    <div className="flex h-full min-h-0 flex-col">
      {error && <QueueError message={error.message} onRetry={onRetry} />}
      <div
        ref={parentRef}
        className="relative flex-1 overflow-auto rounded-md border border-border bg-card"
        role="grid"
        aria-rowcount={alerts.length}
      >
        <AlertsTableHeader sort={sort} onSort={onSort} />
        {loading && alerts.length === 0 ? (
          <SkeletonRows />
        ) : alerts.length === 0 ? (
          <EmptyQueue
            filters={filters}
            onClearFilters={onClearFilters}
            onResetToMyQueue={onResetToMyQueue}
          />
        ) : (
          <div
            style={{ height: rowVirtualizer.getTotalSize(), position: 'relative' }}
            role="rowgroup"
          >
            {rowVirtualizer.getVirtualItems().map((vRow) => {
              const alert = alerts[vRow.index]
              if (!alert) return null
              return (
                <div
                  key={vRow.key}
                  data-index={vRow.index}
                  className="absolute left-0 right-0 top-0"
                  style={{ transform: `translateY(${vRow.start}px)` }}
                  // The virtualizer's positioning wrapper has no
                  // semantic meaning; AlertRow inside is the real
                  // role="row". Marking presentation makes the
                  // rowgroup → row hierarchy unbroken.
                  role="presentation"
                >
                  <AlertRow
                    alert={alert}
                    selected={selectedIds.has(alert.id)}
                    focused={focusedIndex === vRow.index}
                    recentlyChanged={recentlyChangedIds.has(alert.id)}
                    onToggleSelect={(id, range) => {
                      void orderedIds
                      onToggleSelect(id, range)
                    }}
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>
      {/* Pagination sentinel — sits OUTSIDE role="grid" because
          grid children must be row/rowgroup. aria-live="polite"
          alone (no role="status") is enough for AT to announce
          the "Loading more…" message and avoids the
          aria-required-children axe violation. */}
      {alerts.length > 0 && (isFetchingNextPage || hasNextPage) && (
        <div
          className="flex h-10 items-center justify-center gap-3 border-x border-b border-border bg-card text-xs text-muted-fg"
          aria-live="polite"
        >
          {isFetchingNextPage ? (
            <span>Loading more alerts…</span>
          ) : (
            <button
              type="button"
              onClick={() => onLoadMore?.()}
              className="rounded-sm border border-border px-3 py-1 hover:bg-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Load more alerts
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function SkeletonRows() {
  // Skeletons live inside role="grid"; aria-required-children allows
  // only role="row" / role="rowgroup" children. Wrap the placeholder
  // rows in a rowgroup, mark each placeholder a presentational row,
  // and suppress the per-Skeleton role="status" — a single
  // aria-busy/aria-live announcement on the rowgroup is enough.
  return (
    <div role="rowgroup" aria-busy="true" aria-live="polite" aria-label="Loading alerts">
      {Array.from({ length: 14 }).map((_, i) => (
        <div
          key={i}
          role="row"
          aria-hidden="true"
          className="grid h-10 items-center gap-3 border-b border-border/60 px-3 grid-cols-[40px_48px_96px_minmax(20rem,1.4fr)_56px_minmax(10rem,1fr)_128px_56px_24px_112px_80px_80px]"
        >
          <Skeleton role="presentation" className="h-4 w-4 rounded-sm" />
          <Skeleton role="presentation" className="h-2 w-2 rounded-full" />
          <Skeleton role="presentation" className="h-3 w-20" />
          <Skeleton role="presentation" className="h-4 w-3/4" />
          <Skeleton role="presentation" className="h-3 w-8 ml-auto" />
          <Skeleton role="presentation" className="h-4 w-3/4" />
          <Skeleton role="presentation" className="h-3 w-20 ml-auto" />
          <Skeleton role="presentation" className="h-5 w-12 rounded-sm" />
          <Skeleton role="presentation" className="h-3 w-3 rounded-full" />
          <Skeleton role="presentation" className="h-3 w-16" />
          <Skeleton role="presentation" className="h-6 w-6 rounded-full" />
          <Skeleton role="presentation" className="h-3 w-12" />
        </div>
      ))}
    </div>
  )
}
